import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { useAtomValue } from "jotai";

import BrieConversationAPI from "../../../api/api/BrieConversationAPI";
import { InvalidateGPTProjectQueries } from "./useCreateProject";
import { getErrorMessage } from "../../../utils/GeneralUtils";
import { BrieGPTTabs } from "./BrieGPTSidebarElements/Types";
import { tenantIDAtom } from "../../../atoms/GlobalAtoms";
import { newToastEvent } from "../../../utils/ToastUtil";
import { AppConfirm } from "../../../utils/PopupUtil";
import { BrieTypeProps } from "./BrieGPTSidebar";

type MutationProps = BrieTypeProps & { projectID: string };

// Used to a delete Brie GPT Project
const useDeleteGPTProject = (tab: BrieGPTTabs) => {
  const [params, setParams] = useSearchParams();
  const queryClient = useQueryClient();
  const tenantID = useAtomValue(tenantIDAtom) || "";

  const deleteGPTProject = useMutation({
    mutationFn: ({ projectID }: MutationProps) => {
      return BrieConversationAPI.deleteBrieGPTProject(projectID);
    },
    onSuccess: (_, props) => {
      const type = props.type;
      const itemID = type === "view" ? props.viewID : props.recordID;

      InvalidateGPTProjectQueries({
        tab,
        tenantID,
        itemID,
        client: queryClient,
      });

      params.delete("projectID");

      setParams(params);

      newToastEvent("success", "Project successfully deleted.");
    },
    onError: () => {
      newToastEvent("error", "Failed to delete chat.");
    },
  });

  function confirmDelete(props: MutationProps) {
    AppConfirm({
      title: "Delete Project",
      text: "Are you sure you want to delete this project? All associated chats will be deleted. This action cannot be undone.",
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
    }).then((result) => {
      if (!result.isConfirmed) return;

      deleteGPTProject.mutate(props);
    });
  }

  return {
    deleteProject: confirmDelete,
    deletingProject: deleteGPTProject.isPending,
    deleteProjectError: deleteGPTProject.error
      ? getErrorMessage(deleteGPTProject.error)
      : null,
  };
};

export default useDeleteGPTProject;
